import type {
  BulkCreateCustomRuleInput,
  BulkDeleteCustomRulesInput,
  BulkUpdateCustomRulesInput,
  CreateCustomRuleInput,
  CustomRulesResponse,
  ResolveInstructionFilesInput,
  ResolveInstructionFilesResponse,
  UpdateCustomRuleInput,
} from '@/lib/custom-rules/schema';
import type { CustomRule } from '@/lib/custom-rules/service';

import {
  BulkCreateCustomRulesResponseSchema,
  BulkMutationCountResponseSchema,
  CustomRuleResponseSchema,
  CustomRulesResponseSchema,
  ResolveInstructionFilesResponseSchema,
} from '@/lib/custom-rules/schema';
import { baseUrl } from '@/lib/env';

export async function fetchCustomRules(
  installationId: number,
  repositoryId: number | null,
): Promise<CustomRulesResponse> {
  const url = new URL(`/api/custom-rules/${installationId}`, baseUrl());
  if (repositoryId !== null && repositoryId !== undefined) {
    url.searchParams.set('repositoryId', repositoryId.toString());
  }

  const response = await fetch(url.toString(), {
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    let errorMessage = 'Failed to fetch custom agents';
    try {
      const errorPayload = await response.json();
      if (errorPayload?.error) {
        errorMessage = errorPayload.error;
      }
    } catch {
      // Ignore JSON parse errors and fall back to default message.
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return CustomRulesResponseSchema.parse(data);
}

const jsonHeaders = {
  'Content-Type': 'application/json',
};

/** Parses a custom agents API response with `schema`, or throws the API's error message. */
export async function parseResponse<T>(
  response: Response,
  schema: { parse: (data: unknown) => T },
  defaultMessage: string,
): Promise<T> {
  if (!response.ok) {
    let errorMessage = defaultMessage;
    try {
      const errorPayload = await response.json();
      if (errorPayload?.error) {
        errorMessage = errorPayload.error;
      }
    } catch {
      // Ignore parse errors and use default message.
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return schema.parse(data);
}

export async function createCustomRuleRequest(
  installationId: number,
  payload: CreateCustomRuleInput,
): Promise<CustomRule> {
  const response = await fetch(`${baseUrl()}/api/custom-rules/${installationId}`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  const data = await parseResponse(
    response,
    CustomRuleResponseSchema,
    'Failed to create custom agent',
  );
  return data.rule;
}

export async function updateCustomRuleRequest(
  installationId: number,
  ruleId: string,
  payload: UpdateCustomRuleInput,
): Promise<CustomRule> {
  const response = await fetch(`${baseUrl()}/api/custom-rules/${installationId}/${ruleId}`, {
    method: 'PATCH',
    credentials: 'include',
    cache: 'no-store',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  const data = await parseResponse(
    response,
    CustomRuleResponseSchema,
    'Failed to update custom agent',
  );
  return data.rule;
}

export async function deleteCustomRuleRequest(
  installationId: number,
  ruleId: string,
): Promise<void> {
  const response = await fetch(`${baseUrl()}/api/custom-rules/${installationId}/${ruleId}`, {
    method: 'DELETE',
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    let errorMessage = 'Failed to delete custom agent';
    try {
      const errorPayload = await response.json();
      if (errorPayload?.error) {
        errorMessage = errorPayload.error;
      }
    } catch {
      // Ignore parse errors
    }
    throw new Error(errorMessage);
  }
}

export async function createCustomRuleBulkRequest(
  installationId: number,
  payload: BulkCreateCustomRuleInput,
) {
  const response = await fetch(`${baseUrl()}/api/custom-rules/${installationId}/bulk`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  return parseResponse(
    response,
    BulkCreateCustomRulesResponseSchema,
    'Failed to bulk create custom agents',
  );
}

export async function updateCustomRulesBulkRequest(
  installationId: number,
  payload: BulkUpdateCustomRulesInput,
) {
  const response = await fetch(`${baseUrl()}/api/custom-rules/${installationId}/bulk`, {
    method: 'PATCH',
    credentials: 'include',
    cache: 'no-store',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  return parseResponse(
    response,
    BulkMutationCountResponseSchema,
    'Failed to bulk update custom agents',
  );
}

export async function deleteCustomRulesBulkRequest(
  installationId: number,
  payload: BulkDeleteCustomRulesInput,
) {
  const response = await fetch(`${baseUrl()}/api/custom-rules/${installationId}/bulk`, {
    method: 'DELETE',
    credentials: 'include',
    cache: 'no-store',
    headers: jsonHeaders,
    body: JSON.stringify(payload),
  });

  return parseResponse(
    response,
    BulkMutationCountResponseSchema,
    'Failed to bulk delete custom agents',
  );
}

export async function resolveInstructionFilesRequest(
  installationId: number,
  payload: ResolveInstructionFilesInput,
): Promise<ResolveInstructionFilesResponse> {
  const response = await fetch(
    `${baseUrl()}/api/custom-rules/${installationId}/resolve-instruction-files`,
    {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      headers: jsonHeaders,
      body: JSON.stringify(payload),
    },
  );

  return parseResponse(
    response,
    ResolveInstructionFilesResponseSchema,
    'Failed to resolve instruction files',
  );
}
